import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Calendar,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownField,
} from '@roger/ui-components';
import type { VirtualizedComboboxOption } from '@roger/ui-components';
import { Plus, X } from 'lucide-react';

import { isOrderType } from '../constants/visitTypes';
import type { VisitTypeId } from '../services/api/types.gen';
import type { TaskTypeOption } from '../hooks/useVisitTypeOptions';
import {
  TASK_TYPE_VISIT_FILTER_TABS,
  createDefaultTaskTypeFilter,
  type TaskTypeFilterValue,
} from './task/taskTypeFilters';
import { TaskTypeCombobox } from './task/TaskTypeCombobox';
import type { CalendarCreateVisitClinician } from './CalendarCreateVisitDialog';
import { cliniciansToDropdownOptions } from '../utils/clinicianUtils';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { bulkCreatePatientVisitsPatientsPatientIdVisitsBulkPost } from '../services/api/sdk.gen';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import {
  buildBulkVisitItems,
  countVisits,
  setDatesForType,
  type StagedVisitGroup,
} from './scheduleVisitsBuilder';

interface ScheduleVisitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  episodeId: string;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Merged task type options; order types are filtered out internally. */
  visitTypeOptions: TaskTypeOption[];
  clinicians: CalendarCreateVisitClinician[];
  /** Display names shared by more than one clinician (shows email to disambiguate). */
  duplicateClinicianNames?: Set<string>;
  /** Called after visits are created so the parent can refetch. */
  onCreated: () => void | Promise<unknown>;
}

/**
 * Schedules several visits at once: pick a visit type and click the dates for
 * it, repeat for other types, then create them all with one shared assignee in
 * a single request. Each type's dates are staged live as they are selected;
 * re-selecting a staged type shows its dates highlighted for editing.
 */
export const ScheduleVisitsDialog = ({
  open,
  onOpenChange,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  visitTypeOptions,
  clinicians,
  duplicateClinicianNames,
  onCreated,
}: ScheduleVisitsDialogProps) => {
  const assigneeFieldId = useId();
  const visitTypeFieldId = useId();

  const [assigneeId, setAssigneeId] = useState('');
  const [selectedVisitType, setSelectedVisitType] = useState<VisitTypeId | ''>(
    ''
  );
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilterValue>(
    () => createDefaultTaskTypeFilter()
  );
  const [visitTypeOpen, setVisitTypeOpen] = useState(false);
  const [groups, setGroups] = useState<StagedVisitGroup[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // The staged visit type whose row is currently hovered; its dates get a light
  // purple background on the calendar so the user can see them at a glance.
  const [hoveredVisitType, setHoveredVisitType] = useState<VisitTypeId | null>(
    null
  );

  const resetState = useCallback(() => {
    setAssigneeId('');
    setSelectedVisitType('');
    setTaskTypeFilter(createDefaultTaskTypeFilter());
    setVisitTypeOpen(false);
    setGroups([]);
    setHoveredVisitType(null);
    setCreateError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      setIsCreating(false);
    }
  }, [open, resetState]);

  // Show at least two months of dates, and a third when the viewport is wide
  // enough to fit it, so users can pick across months without paging.
  const [monthsToShow, setMonthsToShow] = useState(2);
  useEffect(() => {
    const computeMonths = () => {
      const width = window.innerWidth;
      setMonthsToShow(width >= 1024 ? 3 : width >= 640 ? 2 : 1);
    };
    computeMonths();
    window.addEventListener('resize', computeMonths);
    return () => window.removeEventListener('resize', computeMonths);
  }, []);

  // Only creatable visit types can be scheduled in bulk; orders/communications
  // are single-date tasks created from the Add Task dialog, and legacy visit
  // types would fail the whole batch server-side.
  const visitOnlyOptions = useMemo(
    () =>
      visitTypeOptions.filter(
        option => !isOrderType(option.id) && !option.is_legacy
      ),
    [visitTypeOptions]
  );

  const episodeBounds = useMemo(() => {
    const start = episodeStartDate
      ? parseISOToLocalDate(episodeStartDate)
      : null;
    const end = episodeEndDate ? parseISOToLocalDate(episodeEndDate) : null;
    return { start, end };
  }, [episodeStartDate, episodeEndDate]);

  const disabledDays = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (episodeBounds.start) matchers.push({ before: episodeBounds.start });
    if (episodeBounds.end) matchers.push({ after: episodeBounds.end });
    return matchers;
  }, [episodeBounds]);

  const defaultMonth = useMemo(() => {
    const anchor = episodeBounds.start;
    if (!anchor) return undefined;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  }, [episodeBounds]);

  const clinicianOptions = useMemo(
    () =>
      cliniciansToDropdownOptions(
        clinicians,
        duplicateClinicianNames ?? new Set<string>()
      ),
    [clinicians, duplicateClinicianNames]
  );

  const selectedTypeDisplayName = useMemo(
    () =>
      visitOnlyOptions.find(option => option.id === selectedVisitType)
        ?.display_name ?? selectedVisitType,
    [visitOnlyOptions, selectedVisitType]
  );

  // Dates shown/edited on the calendar belong to the currently selected visit
  // type. Picking a type that is already staged surfaces its dates highlighted.
  const selectedDates = useMemo(
    () =>
      groups.find(group => group.visitType === selectedVisitType)?.dates ?? [],
    [groups, selectedVisitType]
  );

  // Dates already staged for the OTHER visit types, so the user can see which
  // days already have a visit (rendered with a dashed purple border) while
  // editing the current type.
  const otherTypeDates = useMemo(
    () =>
      groups
        .filter(group => group.visitType !== selectedVisitType)
        .flatMap(group => group.dates),
    [groups, selectedVisitType]
  );

  // Dates for the hovered staged type, highlighted on the calendar so hovering a
  // row shows its visits at a glance. Empty when nothing is hovered.
  const hoveredDates = useMemo(
    () =>
      groups.find(group => group.visitType === hoveredVisitType)?.dates ?? [],
    [groups, hoveredVisitType]
  );

  const handleDatesChange = (dates: Date[]) => {
    if (!selectedVisitType) return;
    setGroups(prev =>
      setDatesForType(
        prev,
        selectedVisitType as VisitTypeId,
        selectedTypeDisplayName,
        dates
      )
    );
  };

  const handleRemoveGroup = (visitType: VisitTypeId) => {
    setGroups(prev => prev.filter(group => group.visitType !== visitType));
  };

  const totalVisits = countVisits(groups);

  const handleCreate = async () => {
    if (totalVisits === 0 || !assigneeId) return;
    setCreateError(null);
    setIsCreating(true);
    try {
      const visits = buildBulkVisitItems(groups, assigneeId);
      const response =
        await bulkCreatePatientVisitsPatientsPatientIdVisitsBulkPost({
          path: { patient_id: patientId },
          body: {
            episode_id: episodeId,
            visits,
          },
        });
      if (response.error) {
        console.error('Failed to create visits:', response.error);
        setCreateError(
          getBusinessErrorMessage(response) ?? 'Failed to create visits.'
        );
        return;
      }
      await onCreated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating visits:', error);
      setCreateError('Failed to create visits.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isCreating) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-3xl lg:max-w-5xl'>
        <DialogHeader>
          <DialogTitle>Schedule Visits</DialogTitle>
          <DialogDescription className='sr-only'>
            Pick a visit type and click its dates, repeat for other types, then
            create every visit at once with one assignee.
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-1'>
          <div>
            <DropdownField
              label='Assignee'
              htmlFor={assigneeFieldId}
              variant='formSelect'
              sortOptions
              placeholder='Select assignee'
              options={clinicianOptions}
              value={assigneeId}
              onChange={setAssigneeId}
              disabled={isCreating}
              renderOption={option => {
                const o = option as VirtualizedComboboxOption & {
                  user_email?: string | null;
                  isDup?: boolean;
                };
                return (
                  <>
                    {o.label}
                    {o.isDup && o.user_email && (
                      <span className='ml-1 text-xs text-muted-foreground'>
                        ({o.user_email})
                      </span>
                    )}
                  </>
                );
              }}
            />
          </div>

          <div className='rounded-md border border-input p-3'>
            <div>
              <label className='text-sm font-medium' htmlFor={visitTypeFieldId}>
                Visit Type
              </label>
              <div className='mt-1 flex items-center gap-2'>
                <div className='min-w-0 flex-1'>
                  <TaskTypeCombobox
                    id={visitTypeFieldId}
                    options={visitOnlyOptions}
                    value={selectedVisitType}
                    onValueChange={option =>
                      setSelectedVisitType(option.id as VisitTypeId)
                    }
                    filter={taskTypeFilter}
                    onFilterChange={setTaskTypeFilter}
                    tabs={TASK_TYPE_VISIT_FILTER_TABS}
                    modal
                    disabled={isCreating}
                    placeholder='Select visit type'
                    open={visitTypeOpen}
                    onOpenChange={setVisitTypeOpen}
                  />
                </div>
                {groups.length > 0 && (
                  <Button
                    type='button'
                    variant='outline'
                    className='shrink-0'
                    onClick={() => {
                      setSelectedVisitType('');
                      setTaskTypeFilter(createDefaultTaskTypeFilter());
                      setVisitTypeOpen(true);
                    }}
                    disabled={isCreating}
                  >
                    <Plus className='mr-1 h-4 w-4' />
                    Add another
                  </Button>
                )}
              </div>
            </div>

            <div className='mt-3'>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Dates</label>
                <span className='text-xs text-muted-foreground'>
                  {selectedVisitType
                    ? `${selectedDates.length} selected`
                    : 'Select a visit type first'}
                </span>
              </div>
              <div className='mt-1 flex justify-center overflow-x-auto rounded-md border border-input'>
                <Calendar
                  mode='multiple'
                  numberOfMonths={monthsToShow}
                  pagedNavigation
                  selected={selectedDates}
                  onSelect={dates => handleDatesChange(dates ?? [])}
                  defaultMonth={defaultMonth}
                  disabled={disabledDays}
                  showOutsideDays={false}
                  modifiers={{
                    scheduledOtherType: otherTypeDates,
                    hoveredType: hoveredDates,
                  }}
                  modifiersStyles={{
                    scheduledOtherType: {
                      outline: '2px dashed rgb(124 58 237)',
                      outlineOffset: '-2px',
                      borderRadius: '6px',
                    },
                    hoveredType: {
                      backgroundColor: 'rgb(221 214 254)',
                      borderRadius: '6px',
                    },
                  }}
                />
              </div>
              {otherTypeDates.length > 0 && (
                <div className='mt-2 flex items-center gap-1.5 text-xs text-muted-foreground'>
                  <span
                    className='inline-block h-3 w-3 rounded-sm'
                    style={{
                      outline: '2px dashed rgb(124 58 237)',
                      outlineOffset: '-2px',
                    }}
                  />
                  <span>Already scheduled for another visit type</span>
                </div>
              )}
            </div>
          </div>

          {groups.length > 0 && (
            <div className='space-y-2'>
              {groups.map(group => {
                const isActive = group.visitType === selectedVisitType;
                return (
                  <div
                    key={group.visitType}
                    role='button'
                    tabIndex={0}
                    onClick={() => setSelectedVisitType(group.visitType)}
                    onMouseEnter={() => setHoveredVisitType(group.visitType)}
                    onMouseLeave={() => setHoveredVisitType(null)}
                    onFocus={() => setHoveredVisitType(group.visitType)}
                    onBlur={() => setHoveredVisitType(null)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedVisitType(group.visitType);
                      }
                    }}
                    className={`flex cursor-pointer items-start justify-between gap-2 rounded-md px-3 py-2 ${
                      isActive
                        ? 'bg-primary/10 ring-1 ring-primary/40'
                        : 'bg-muted/40 hover:bg-muted/60'
                    }`}
                  >
                    <div className='min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium'>
                          {group.displayName}
                        </span>
                        <Badge variant='secondary' className='text-xs'>
                          {group.dates.length}
                        </Badge>
                      </div>
                      <div className='mt-0.5 text-xs text-muted-foreground'>
                        {group.dates
                          .map(date => formatDateToMMDDYYYY(date))
                          .join(', ')}
                      </div>
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-6 w-6 shrink-0 p-0'
                      onClick={event => {
                        event.stopPropagation();
                        handleRemoveGroup(group.visitType);
                      }}
                      disabled={isCreating}
                      aria-label={`Remove ${group.displayName}`}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {createError && (
            <p
              role='alert'
              className='rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive'
            >
              {createError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreate()}
            disabled={isCreating || totalVisits === 0 || !assigneeId}
          >
            {isCreating
              ? 'Creating...'
              : totalVisits > 0
                ? `Create ${totalVisits} ${totalVisits === 1 ? 'visit' : 'visits'}`
                : 'Create visits'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleVisitsDialog;
